// Integration functions
import { UploadedFile } from './entities';

export const Core = {
  InvokeLLM: null, // Will be implemented below
  SendEmail: null,
  UploadFile: null,
  GenerateImage: null,
  ExtractDataFromUploadedFile: null,
  CreateFileSignedUrl: null,
  UploadPrivateFile: null
};

export const InvokeLLM = async (prompt, options = {}) => {
  try {
    // Mock LLM response
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return {
      success: true,
      response: "This is a mock AI response. In a real implementation, this would connect to an LLM service.",
      model: options.model || 'mock-model',
      tokens: Math.floor(Math.random() * 100) + 50,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    throw new Error('LLM invocation failed: ' + error.message);
  }
};

export const SendEmail = async (emailData) => {
  try {
    // Mock email sending
    await new Promise(resolve => setTimeout(resolve, 800));
    
    console.log('Mock email sent:', {
      to: emailData.to,
      subject: emailData.subject,
      from: emailData.from || 'noreply@flightapp.com'
    });
    
    return {
      success: true,
      messageId: Math.random().toString(36).substr(2, 9),
      timestamp: new Date().toISOString(),
      status: 'sent'
    };
  } catch (error) {
    throw new Error('Email sending failed: ' + error.message);
  }
};

export const UploadFile = async ({ file }) => {
  try {
    console.log('🔵 UploadFile called with file:', file?.name, file?.type, file?.size);
    
    const { User } = await import('./entities');
    console.log('🔵 User entity imported');
    
    // Get current user
    const currentUser = await User.getCurrentUser();
    console.log('🔵 Current user:', currentUser?.id);
    
    if (!currentUser) {
      throw new Error('User must be logged in to upload files');
    }
    
    // Get session from Supabase
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No authentication session found');
    }
    
    // Convert file to base64
    console.log('🔵 Converting file to base64...');
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        console.log('🔵 Base64 conversion complete');
        resolve(dataUrl); // Include data URL prefix
      };
      reader.onerror = (err) => {
        console.error('🔴 File read error:', err);
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
    
    console.log('🔵 Uploading via backend API to bypass RLS...');
    
    // Upload via backend using service role (bypasses RLS)
    const response = await fetch('/api/upload-file', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        fileName: file.name || 'itinerary.jpg',
        fileType: file.type || 'image/jpeg',
        fileData: base64
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Backend upload failed');
    }

    const result = await response.json();
    console.log('✅ Upload successful!', result);
    
    return {
      success: true,
      file_id: result.file_id,
      file_url: result.file_url,
      file_base64: result.file_base64, // For immediate OCR use
      file_name: result.file_name,
      file_size: result.file_size
    };
  } catch (error) {
    console.error('🔴 UploadFile error:', error);
    console.error('🔴 Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    throw new Error('File upload failed: ' + error.message);
  }
};

export const GenerateImage = async (prompt, options = {}) => {
  try {
    // Mock image generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const imageId = Math.random().toString(36).substr(2, 9);
    const mockImageUrl = `https://picsum.photos/${options.width || 512}/${options.height || 512}?random=${imageId}`;
    
    return {
      success: true,
      image_id: imageId,
      image_url: mockImageUrl,
      prompt: prompt,
      width: options.width || 512,
      height: options.height || 512,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    throw new Error('Image generation failed: ' + error.message);
  }
};

export const ExtractDataFromUploadedFile = async ({ file_url, file_base64, json_schema }) => {
  try {
    // The route needs a signed-in customer (each call is a paid vision request).
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No authentication session found');
    }

    // Use OpenAI Vision API for real OCR extraction
    const response = await fetch('/api/extract-flight-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        image_base64: file_base64,
        json_schema: json_schema
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Extraction failed with status ${response.status}`);
    }

    const data = await response.json();
    
    // Return data in the format expected by Upload.jsx
    return {
      status: 'success',
      output: data.extracted_data,
      file_url: file_url,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    throw new Error('Data extraction failed: ' + error.message);
  }
};

export const CreateFileSignedUrl = async ({ file_name, file_type }) => {
  try {
    // Mock signed URL creation
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const urlId = Math.random().toString(36).substr(2, 9);
    const signedUrl = `https://mock-storage.example.com/upload/${urlId}?file=${encodeURIComponent(file_name)}&type=${encodeURIComponent(file_type)}`;
    
    return {
      success: true,
      signed_url: signedUrl,
      expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(), // 15 minutes from now
      file_name: file_name,
      file_type: file_type
    };
  } catch (error) {
    throw new Error('Signed URL creation failed: ' + error.message);
  }
};

export const UploadPrivateFile = async ({ file, folder }) => {
  try {
    // Mock private file upload
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const fileId = Math.random().toString(36).substr(2, 9);
    const fileName = file.name || 'private-file';
    const folderPath = folder || 'private';
    const mockUrl = `https://mock-private-storage.example.com/${folderPath}/${fileId}/${encodeURIComponent(fileName)}`;
    
    return {
      success: true,
      file_id: fileId,
      private_url: mockUrl,
      file_name: fileName,
      folder: folderPath,
      file_size: file.size || 0,
      access_level: 'private',
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    throw new Error('Private file upload failed: ' + error.message);
  }
};

// Update Core object with actual implementations
Core.InvokeLLM = InvokeLLM;
Core.SendEmail = SendEmail;
Core.UploadFile = UploadFile;
Core.GenerateImage = GenerateImage;
Core.ExtractDataFromUploadedFile = ExtractDataFromUploadedFile;
Core.CreateFileSignedUrl = CreateFileSignedUrl;
Core.UploadPrivateFile = UploadPrivateFile;






